import * as React from 'react';
import cx from 'classnames';

import { Flex } from '@common/components/Flex';
import { NewTableSortingDirection } from '@common/components/new-table/new-table.types';
import { HeaderProps, useController } from './header.controller';

import './header.styles.scss';

export const Header: React.FC<HeaderProps> = (props) => {
  const { model, loadTableMetadata, columns, currentColumnResizeOffset, onCellContextMenu, onHeaderContextMenu, onColumnResizeEnd } = props;
 
  const initMouseUp = React.useCallback(() => {
    document.addEventListener('mouseup', function handle() {
      if (onColumnResizeEnd) {
        onColumnResizeEnd();
      }

      document.removeEventListener('mouseup', handle);
    });
  }, [onColumnResizeEnd]);

  const {
    handleSort,
    handleCellContextMenu,
    handleHeaderContextMenu,
  } = useController(props);

  return (
    <thead className="NewTable__header">
      {model.map((headerGroup) => (
        <tr 
          key={headerGroup.id}
          onContextMenu={onHeaderContextMenu ? handleHeaderContextMenu : undefined}
        >
          {headerGroup.headers.map((headerCell) => {
            const filteredColumnsId = Object.keys(loadTableMetadata?.filtering?.columns || {});
            const isFiltered = filteredColumnsId?.some((id) => id === headerCell.id);
            const isSorted = loadTableMetadata?.sorting?.id === headerCell.id;
            const sortingDirection = loadTableMetadata?.sorting?.direction;
            const hasSortIcon = isSorted && sortingDirection;
            const originalColumn = columns.find((x) => x.id === headerCell.id);
            const resizable = (headerCell.column.columnDef.enableResizing ?? true);
            const content = typeof headerCell.column.columnDef.header === 'function'
              ? headerCell.column.columnDef.header(headerCell.getContext())
              : headerCell.column.columnDef.header;

            const cellSize = headerCell.getSize();
            const cellSizeWithoutResizerElement = resizable ? cellSize - 16 : cellSize;

            return (
              <th
                key={headerCell.id}
                colSpan={headerCell.colSpan}
                style={{
                  minWidth: cellSize,
                  width: cellSize,
                  maxWidth: cellSize
                }}
                className={cx(
                  'NewTable__header__cell', 
                  {
                    'NewTable__header__cell--sortable': headerCell.column.columnDef.enableSorting,
                    'NewTable__header__cell--resizable': resizable,
                  }
                )}
                title={content?.toString()}
                data-id={headerCell.id}
                onContextMenu={onCellContextMenu && !originalColumn?.disableActions ? handleCellContextMenu : undefined}
              >
                <Flex 
                  data-id={headerCell.id}
                  alignItems="center" 
                  style={{ 
                    minWidth: cellSizeWithoutResizerElement,
                    width: cellSizeWithoutResizerElement,
                    maxWidth: cellSizeWithoutResizerElement
                  }}
                  onClick={headerCell.column.columnDef.enableSorting ? handleSort : undefined}
                >
                  {hasSortIcon && (
                    <span 
                      className={cx(
                        'fa',
                        sortingDirection === NewTableSortingDirection.asc ? 'fa-caret-up' : 'fa-caret-down',
                        'NewTable__header__cell__sort-icon'
                      )} 
                    />
                  )}
                  <div
                    style={{
                      width: `calc(100% - ${hasSortIcon ? '8px' : '0px'} - ${isFiltered ? '8px' : '0px'}})`
                    }}
                    className="NewTable__header__cell__text"
                  >
                    {content}
                  </div>
                  {isFiltered && <span className="fa fa-filter NewTable__header__cell__filter-icon" />}
                </Flex>
                {resizable && (
                  <span
                    onMouseDown={(e) => { // TODO think of better solution
                      if (onColumnResizeEnd) {
                        initMouseUp();
                      }

                      e.stopPropagation();
                      const handle = headerCell.getResizeHandler();
                      handle(e);
                    }}
                    className={cx(
                      'fa',
                      'fa-ellipsis-v',
                      'NewTable__header__cell__resize-icon',
                      { 'NewTable__header__cell__resize-icon--active': headerCell.column.getIsResizing() },
                    )}
                    style={{
                      transform: headerCell.column.getIsResizing() 
                        ? `translateX(${currentColumnResizeOffset}px)`
                        : '',
                    }}
                  />
                )}
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
};